import { useState } from 'react';
import { useSite } from '../../data/SiteContext';
import { DEST_TAGS } from './adminData';
import { apiUpload } from '../../data/api';

const panel = { padding:26, borderRadius:22 };
const REGIONS = ['Caribbean', 'Europe', 'Asia', 'Middle East', 'Africa', 'North America', 'South America', 'Central America', 'Australia & Oceania', 'Polar'];

// editId: an existing destination id (edit) or null (add).
export default function DestinationForm({ editId, onBack }) {
  const { dests, addDest, updateDest } = useSite();
  const existing = editId ? dests.find(d => d.id === editId) : null;

  const [f, setF] = useState({
    name: existing?.name || '',
    country: existing?.country || '',
    region: existing?.region || 'Caribbean',
    tag: existing?.tag || 'Beach & Resort',
    nights: existing ? String(existing.nights) : '7',
    priceFrom: existing ? String(existing.from) : '',
    blurb: existing?.blurb || '',
    coverImageFileId: existing?.coverImageFileId || null,
    coverUrl: existing?.img || null,
    marginPercent: existing?.marginPercent ?? '',
    sources: existing?.sources || '',
    galleryFileIds: existing?.galleryFileIds || [],
    galleryUrls: existing?.galleryUrls || [],
    isPublished: existing ? existing.isPublished : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const onGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setError(null);
    try {
      for (const file of files) {
        const { fileId, url } = await apiUpload(file);
        setF(prev => ({ ...prev, galleryFileIds: [...prev.galleryFileIds, fileId], galleryUrls: [...prev.galleryUrls, url] }));
      }
    } catch (err) { setError(err.message || 'Gallery upload failed.'); }
    e.target.value = '';
  };
  const removeGalleryAt = (i) => setF(prev => ({
    ...prev,
    galleryFileIds: prev.galleryFileIds.filter((_, ix) => ix !== i),
    galleryUrls: prev.galleryUrls.filter((_, ix) => ix !== i),
  }));

  const onCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      const { fileId, url } = await apiUpload(file);
      setF(prev => ({ ...prev, coverImageFileId: fileId, coverUrl: url }));
    } catch (err) {
      setError(err.message || 'Image upload failed.');
    }
  };

  const save = async (publish) => {
    if (saving) return;
    if (!f.name.trim()) { setError('Destination name is required.'); return; }
    if (!f.country.trim()) { setError('Country is required.'); return; }
    setError(null);
    setSaving(true);
    const model = {
      name: f.name.trim(), country: f.country.trim(), region: f.region, tag: f.tag,
      nights: f.nights, from: f.priceFrom, blurb: f.blurb.trim() || null,
      coverImageFileId: f.coverImageFileId, isPublished: publish,
      marginPercent: f.marginPercent === '' || f.marginPercent === null ? null : Number(f.marginPercent),
      sources: f.sources,
      galleryFileIds: f.galleryFileIds,
    };
    try {
      if (editId) await updateDest(editId, model);
      else await addDest(model);
      onBack();
    } catch (err) {
      setError(err.message || 'Save failed.');
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:18, flexWrap:'wrap' }}>
        <span onClick={onBack} style={{ cursor:'pointer', fontSize:13.5, fontWeight:800, color:'#5a726f' }}>← Back to destinations</span>
        {error && <span style={{ marginLeft:'auto', color:'#c0492f', fontSize:13.5, fontWeight:700 }}>{error}</span>}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:18, alignItems:'start' }}>
        <div style={{ gridColumn:'span 2', minWidth:0, display:'grid', gap:18 }}>
          {/* Basics */}
          <div className="glass-card" style={panel}>
            <div style={{ fontWeight:800, fontSize:16, marginBottom:18 }}>Basics</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))', gap:14 }}>
              <label style={{ display:'block' }}><span className="a-label">Destination name</span><input value={f.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Punta Cana" className="a-input" /></label>
              <label style={{ display:'block' }}><span className="a-label">Country</span><input value={f.country} onChange={e => set('country', e.target.value)} placeholder="e.g. Dominican Republic" className="a-input" /></label>
              <label style={{ display:'block' }}><span className="a-label">Region</span><select className="a-input" value={f.region} onChange={e => set('region', e.target.value)}>{REGIONS.map(r => <option key={r}>{r}</option>)}</select></label>
              <label style={{ display:'block' }}><span className="a-label">Nights</span><input value={f.nights} onChange={e => set('nights', e.target.value)} placeholder="7" className="a-input" /></label>
            </div>
            <div style={{ marginTop:16 }}>
              <span className="a-label" style={{ marginBottom:8 }}>Tag</span>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {DEST_TAGS.map(label => {
                  const on = f.tag === label;
                  return <span key={label} onClick={() => set('tag', label)} style={{ padding:'8px 14px', borderRadius:100, fontSize:13, fontWeight:700, cursor:'pointer', ...(on ? { background:'linear-gradient(135deg,#26c6a3,#0e8a9c)', color:'#fff' } : { background:'rgba(255,255,255,0.85)', color:'#5a726f', border:'1px solid #f2e2d2' }) }}>{label}</span>;
                })}
              </div>
            </div>
            <label style={{ display:'block', marginTop:16 }}><span className="a-label">Description</span><textarea rows={4} value={f.blurb} onChange={e => set('blurb', e.target.value)} placeholder="What makes this destination special?" className="a-input" style={{ resize:'vertical' }} /></label>
            <label style={{ display:'block', marginTop:16 }}><span className="a-label">Source references (where the facts came from)</span><textarea rows={3} value={f.sources} onChange={e => set('sources', e.target.value)} placeholder={'https://…  (one URL per line)'} className="a-input" style={{ resize:'vertical' }} /></label>
            {(f.sources || '').split('\n').map(s => s.trim()).filter(s => /^https?:\/\//.test(s)).length > 0 && (
              <div style={{ display:'grid', gap:4, marginTop:6 }}>
                {(f.sources || '').split('\n').map(s => s.trim()).filter(s => /^https?:\/\//.test(s)).map(u => (
                  <a key={u} href={u} target="_blank" rel="noreferrer" style={{ fontSize:12.5, color:'#0e8a9c', fontWeight:700, overflowWrap:'anywhere' }}>{u}</a>
                ))}
              </div>
            )}
          </div>

          {/* Pricing */}
          <div className="glass-card" style={panel}>
            <div style={{ fontWeight:800, fontSize:16, marginBottom:16 }}>Pricing</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:14 }}>
              <label style={{ display:'block' }}><span className="a-label">Price from ($)</span><input value={f.priceFrom} onChange={e => set('priceFrom', e.target.value)} placeholder="899" className="a-input" /></label>
            </div>

            <div style={{ marginTop:18, paddingTop:16, borderTop:'1px solid rgba(14,138,122,0.12)' }}>
              <label style={{ display:'block', maxWidth:260 }}>
                <span className="a-label">Hotel margin for this destination (%)</span>
                <input type="number" min="0" max="500" value={f.marginPercent}
                  onChange={e => set('marginPercent', e.target.value)}
                  placeholder="Using the agency default" className="a-input" />
              </label>
              <div style={{ fontSize:12.5, color:'#5a726f', marginTop:8, maxWidth:'62ch' }}>
                Added to supplier hotel prices for every hotel in this destination. Leave blank to follow the
                agency default; an individual hotel can still override it.
                {existing?.hotelPriceFrom != null && <> Live hotels here currently start at <b>\${existing.hotelPriceFrom}</b>.</>}
              </div>
            </div>
          </div>
        </div>

        {/* Side rail */}
        <div style={{ display:'grid', gap:18, alignContent:'start' }}>
          <div className="glass-card" style={{ padding:24, borderRadius:22 }}>
            <div style={{ fontWeight:800, fontSize:16, marginBottom:14 }}>Publish</div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, padding:'14px 16px', borderRadius:14, background:'rgba(255,255,255,0.7)', border:'1px solid rgba(255,255,255,0.9)' }}>
              <div><div style={{ fontWeight:700, fontSize:14.5 }}>{f.isPublished ? 'Published' : 'Draft'}</div><div style={{ fontSize:12.5, color:'#5a726f' }}>Visible on the public site</div></div>
              <div onClick={() => set('isPublished', !f.isPublished)} style={{ width:52, height:30, borderRadius:100, padding:3, cursor:'pointer', display:'flex', flexShrink:0, background: f.isPublished ? 'linear-gradient(135deg,#26c6a3,#0e8a9c)' : '#d7e6e2', justifyContent: f.isPublished ? 'flex-end' : 'flex-start' }}>
                <div style={{ width:24, height:24, borderRadius:'50%', background:'#fff', boxShadow:'0 2px 6px rgba(6,40,45,0.3)' }}></div>
              </div>
            </div>
            <button onClick={() => save(true)} disabled={saving} className="a-primary" style={{ width:'100%', marginTop:14, padding:14, borderRadius:13, fontSize:14.5, opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : (editId ? 'Save changes' : 'Save destination')}</button>
            <button onClick={() => save(false)} disabled={saving} className="a-ghost" style={{ width:'100%', marginTop:9, padding:13, borderRadius:13, fontSize:14 }}>Save as draft</button>
          </div>
          <div className="glass-card" style={{ padding:24, borderRadius:22 }}>
            <div style={{ fontWeight:800, fontSize:16, marginBottom:12 }}>Cover image</div>
            {f.coverUrl
              ? <div style={{ height:150, borderRadius:14, backgroundImage:`url(${f.coverUrl})`, backgroundSize:'cover', backgroundPosition:'center', marginBottom:10 }}></div>
              : <div style={{ height:150, borderRadius:14, border:'1px dashed rgba(14,138,122,0.4)', background:'rgba(255,255,255,0.55)', display:'flex', alignItems:'center', justifyContent:'center', color:'#5a726f', fontWeight:700, fontSize:13.5, marginBottom:10 }}>No cover yet</div>}
            <label className="a-ghost" style={{ display:'block', textAlign:'center', padding:11, borderRadius:12, fontSize:13.5, cursor:'pointer' }}>
              Upload image
              <input type="file" accept="image/*" onChange={onCover} style={{ display:'none' }} />
            </label>
          </div>
          <div className="glass-card" style={{ padding:24, borderRadius:22 }}>
            <div style={{ fontWeight:800, fontSize:16, marginBottom:12 }}>Gallery ({f.galleryFileIds.length})</div>
            {f.galleryUrls.length > 0 && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
                {f.galleryUrls.map((u, i) => (
                  <div key={u + i} style={{ position:'relative', height:76, borderRadius:10, overflow:'hidden' }}>
                    <div className="bgimg" style={{ backgroundImage:`url(${u})` }}></div>
                    <span onClick={() => removeGalleryAt(i)} title="Remove"
                      style={{ position:'absolute', top:4, right:4, width:22, height:22, borderRadius:'50%', background:'rgba(6,40,45,0.75)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800, cursor:'pointer' }}>×</span>
                  </div>
                ))}
              </div>
            )}
            <label className="a-ghost" style={{ display:'block', textAlign:'center', padding:11, borderRadius:12, fontSize:13.5, cursor:'pointer' }}>
              Add photos
              <input type="file" accept="image/*" multiple onChange={onGallery} style={{ display:'none' }} />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
